import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Search, Package, ArrowRight, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import { usePageTracking } from '../hooks/usePageTracking';

export const ProduitsUbiquitiPage = () => {
  usePageTracking();

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [imageMapping, setImageMapping] = useState({});
  const productsPerPage = 24;

  useEffect(() => {
    // Load products and image mapping
    Promise.all([
      fetch('/data/ubiquiti-products.json').then(res => res.json()),
      fetch('/data/product-images.json').then(res => res.json())
    ])
      .then(([productsData, imagesData]) => {
        setProducts(productsData);
        setFilteredProducts(productsData);
        setImageMapping(imagesData);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading data:', err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    let filtered = [...products];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(p => 
        String(p['Device SKUs']).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
    setCurrentPage(1); // Reset to first page on filter change
  }, [searchTerm, products]);

  // Pagination
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 network-lines opacity-5"></div>
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-500 opacity-20 blur-[150px] rounded-full"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Package className="w-12 h-12 text-[#FF6B35]" />
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF6B35]">
                Catalogue Complet
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 tracking-tight">
              Produits <span className="text-[#FF6B35]">Ubiquiti</span>
            </h1>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              Explorez notre catalogue de plus de 1300 produits authentiques Ubiquiti. 
              Distributeur officiel et installateur autorisé.
            </p>
          </motion.div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 max-w-3xl mx-auto">
            <div className="glass rounded-xl p-4 text-center border border-white/10">
              <div className="text-2xl font-black text-[#FF6B35]">{products.length}</div>
              <div className="text-xs text-gray-400">Produits</div>
            </div>
            <div className="glass rounded-xl p-4 text-center border border-white/10">
              <div className="text-2xl font-black text-[#FF6B35]">24/7</div>
              <div className="text-xs text-gray-400">Support</div>
            </div>
            <div className="glass rounded-xl p-4 text-center border border-white/10">
              <div className="text-2xl font-black text-[#FF6B35]">100%</div>
              <div className="text-xs text-gray-400">Authentique</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters & Search */}
      <section className="py-8 bg-[#0A0A0A] sticky top-0 z-30 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par SKU (ex: U6+, Dream Machine...)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-[#121212] border border-white/10 rounded-lg pl-12 pr-4 py-3 text-white placeholder-gray-500 focus:border-[#FF6B35] focus:outline-none"
              />
            </div>
          </div>

          {/* Results count */}
          <div className="mt-4 text-sm text-gray-400">
            {filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''} trouvé{filteredProducts.length > 1 ? 's' : ''}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF6B35]"></div>
              <p className="text-gray-400 mt-4">Chargement des produits...</p>
            </div>
          ) : currentProducts.length === 0 ? (
            <div className="text-center py-20">
              <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">Aucun produit trouvé</p>
              <p className="text-gray-500 text-sm mt-2">Essayez de modifier vos filtres de recherche</p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                {currentProducts.map((product, index) => {
                  const sku = product['Device SKUs'];
                  const imagePath = imageMapping[sku] || '/images/products/placeholder.png';
                  
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.02 }}
                      className="bg-[#121212] border border-white/10 rounded-xl p-6 hover:border-[#FF6B35]/50 transition-all duration-300 hover:-translate-y-1 group"
                    >
                      {/* Product Image */}
                      <div className="mb-4">
                        <div className="w-full h-48 bg-[#0A0A0A] rounded-lg flex items-center justify-center overflow-hidden mb-3 group-hover:bg-[#1a1a1a] transition-colors">
                          <img 
                            src={imagePath} 
                            alt={sku}
                            className="max-w-full max-h-full object-contain p-4 group-hover:scale-110 transition-transform duration-300"
                            onError={(e) => {
                              e.target.src = '/images/products/placeholder.png';
                            }}
                          />
                        </div>
                        <h3 className="font-bold text-white text-lg mb-1 group-hover:text-[#FF6B35] transition-colors">
                          {sku}
                        </h3>
                        <p className="text-sm text-gray-400 mb-4">Produit Ubiquiti authentique</p>
                      </div>

                      <button
                        onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                        className="w-full bg-[#FF6B35]/10 hover:bg-[#FF6B35] text-[#FF6B35] hover:text-white font-semibold py-2 rounded-lg transition-all text-sm"
                      >
                        Demander un devis
                      </button>
                    </motion.div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-12">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded-lg bg-[#121212] border border-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:border-[#FF6B35] transition-colors"
                  >
                    Précédent
                  </button>
                  
                  <div className="flex gap-2">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-4 py-2 rounded-lg border transition-colors ${
                            currentPage === pageNum
                              ? 'bg-[#FF6B35] border-[#FF6B35] text-white'
                              : 'bg-[#121212] border-white/10 text-white hover:border-[#FF6B35]'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 rounded-lg bg-[#121212] border border-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:border-[#FF6B35] transition-colors"
                  >
                    Suivant
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0A0A0A] relative overflow-hidden">
        <div className="absolute inset-0 network-lines opacity-5"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Shield className="w-12 h-12 text-[#FF6B35] mx-auto mb-6" />
            <h2 className="text-3xl sm:text-4xl font-black mb-6">
              Besoin d'aide pour <span className="text-[#FF6B35]">choisir</span> ?
            </h2>
            <p className="text-lg text-gray-400 mb-8">
              Notre équipe certifiée Ubiquiti vous accompagne dans la sélection des produits adaptés à votre infrastructure.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setTimeout(() => {
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="bg-[#FF6B35] text-white font-semibold px-8 py-4 rounded-lg hover:bg-[#FF8555] transition-all hover:shadow-[0_0_20px_rgba(255,107,53,0.4)] inline-flex items-center justify-center gap-2"
              >
                Contactez notre équipe certifiée
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href="tel:+14503599394"
                className="bg-transparent border-2 border-white/20 text-white font-semibold px-8 py-4 rounded-lg hover:border-white transition-all inline-flex items-center justify-center gap-2"
              >
                (450) 359-9394
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
